import { useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom'

import { DispatchLanguage } from '../languages/config/DispatchLanguage';
import { authController, authMultiCompany } from '../scripts/authentication.script';
import { appLanguageRouteUpload, appLanguageRouteIsValid } from '../scripts/app.language.script';
import { useSubscription } from '../contexts/SubscriptionContext';

export type RouteProps = {
  content: JSX.Element
};

export type MainProps = {
  content: JSX.Element,
  multicompany: boolean
};

export type SubscriptionGuardProps = {
  action: string,
  arn: string,
  content: JSX.Element,
  roleFallback?: JSX.Element,
  planFallback?: JSX.Element,
};

export const AuthenticationRoute = ({content}: RouteProps) => {
  const ctrl = authController()
  const multi = ctrl ? authMultiCompany() : false
  return !ctrl ? content : <Navigate to={multi ? '/main/dashboard' : '/main/welcome'} replace/>
};

export const AuthorizationRoute = ({content}: RouteProps) => {
  return authController() ? content : <Navigate to={'/auth/login'} replace/>
};

export const ValidatedRoute = ({content}: RouteProps) => {
  return authController() ? authMultiCompany() ? content : <Navigate to={'/main/welcome'} replace/> : <Navigate to={'/auth/login'} replace/>
};

export const MainRoute = ({content, multicompany}: MainProps) => {
  const ctrl = authController()
  const multi = ctrl ? authMultiCompany() : false
  return ctrl ? multi === multicompany ? content : <Navigate to={multi ? '/main/dashboard' : '/main/welcome'} replace/> : <Navigate to={'/auth/login'} replace/>
};

// Hooks first, redirections after — React requires hooks to run on every render.
export const SubscriptionGuard = ({action, arn, content, roleFallback, planFallback}: SubscriptionGuardProps) => {
  const { can: canSub } = useSubscription()
  if (!authController()) return <Navigate to={'/auth/login'} replace/>
  if (!authMultiCompany()) return <Navigate to={'/main/welcome'} replace/>
  const result = canSub(action, arn)
  if (!result.allowed) {
    if (result.reason === 'plan' || result.reason === 'status') {
      return planFallback ?? <Navigate to={'/main/dashboard'} replace/>
    }
    // role denial
    return roleFallback ?? <Navigate to={'/main/dashboard'} replace/>
  }
  return content
};

export const LanguageRoute = () => {
  const dispatchLanguage = DispatchLanguage()
  const location = useLocation()
  const param = useParams()
  const lastLang = useRef<string | undefined>(undefined)

  // Dispatch synchronously on first render and when language changes,
  // so children always render with the correct locale from the URL.
  if (param.language !== lastLang.current) {
    lastLang.current = param.language
    appLanguageRouteUpload(dispatchLanguage, param.language)
  }

  return appLanguageRouteIsValid(param.language) ? <Outlet/> : <Navigate to={`/${location.pathname.split('/').slice(2).join('/')}`}/>
}
